import {
  ATR,
  RSI,
  ADX,
  EMA,
  EMACloud,
  SMA,
  Bollinger,
  DSS,
  KeyLevels,
  Trendline,
  TrendlineV1,
  Swing,
  VWAP,
  getCycleHighEventDefinitions,
  getCycleLowEventDefinitions,
} from '@andywilliams/indicators';
import { DIVERGENCE_EVENT_DEFINITIONS } from './divergenceEvents';

const LIVE_CYCLE_EVENT_DEFINITIONS = [
  { id: 'cycle.low.provisional', name: 'Cycle Low (Live) Provisional', description: 'A new provisional weekly cycle low pivot was detected.', category: 'cycles' },
  { id: 'cycle.low.developing.33', name: 'Cycle Low (Live) 33%', description: 'Live cycle low confidence reached 33%.', category: 'cycles' },
  { id: 'cycle.low.developing.66', name: 'Cycle Low (Live) 66%', description: 'Live cycle low confidence reached 66%.', category: 'cycles' },
  { id: 'cycle.low.developing.100', name: 'Cycle Low (Live) 100%', description: 'Live cycle low confidence reached 100%.', category: 'cycles' },
  { id: 'cycle.low.confirmed', name: 'Cycle Low (Live) Confirmed', description: 'Live cycle low pivot met confirmation criteria.', category: 'cycles' },
  { id: 'cycle.low.superseded', name: 'Cycle Low (Live) Superseded', description: 'A newer pivot replaced the previous active cycle low.', category: 'cycles' },
  { id: 'cycle.high.provisional', name: 'Cycle High (Live) Provisional', description: 'A new provisional weekly cycle high pivot was detected.', category: 'cycles' },
  { id: 'cycle.high.developing.33', name: 'Cycle High (Live) 33%', description: 'Live cycle high confidence reached 33%.', category: 'cycles' },
  { id: 'cycle.high.developing.66', name: 'Cycle High (Live) 66%', description: 'Live cycle high confidence reached 66%.', category: 'cycles' },
  { id: 'cycle.high.developing.100', name: 'Cycle High (Live) 100%', description: 'Live cycle high confidence reached 100%.', category: 'cycles' },
  { id: 'cycle.high.confirmed', name: 'Cycle High (Live) Confirmed', description: 'Live cycle high pivot met confirmation criteria.', category: 'cycles' },
  { id: 'cycle.high.superseded', name: 'Cycle High (Live) Superseded', description: 'A newer pivot replaced the previous active cycle high.', category: 'cycles' },
];

const INDICATOR_MODULES = [
  { key: 'swing', category: 'swings', module: Swing },
  { key: 'keyLevels', category: 'marketStructure', module: KeyLevels },
  { key: 'trendline', category: 'trendlines', module: Trendline },
  { key: 'trendlineV1', category: 'trendlines', module: TrendlineV1 },
  { key: 'ema', category: 'movingAverages', module: EMA },
  { key: 'emaCloud', category: 'movingAverages', module: EMACloud },
  { key: 'sma', category: 'movingAverages', module: SMA },
  { key: 'vwap', category: 'vwap', module: VWAP },
  { key: 'bollinger', category: 'volatility', module: Bollinger },
  { key: 'atr', category: 'volatility', module: ATR },
  { key: 'dss', category: 'oscillators', module: DSS },
  { key: 'rsi', category: 'oscillators', module: RSI },
  { key: 'adx', category: 'oscillators', module: ADX },
  // Divergence at confirmed daily cycle pivots (DWLF-338): stored by the jobs, one id set per oscillator.
  {
    key: 'divergence',
    category: 'oscillators',
    module: { getEventDefinitions: () => DIVERGENCE_EVENT_DEFINITIONS },
  },
  { key: 'cycleLow', category: 'cycles', module: { getEventDefinitions: getCycleLowEventDefinitions } },
  { key: 'cycleHigh', category: 'cycles', module: { getEventDefinitions: getCycleHighEventDefinitions } },
  { key: 'liveCycle', category: 'cycles', module: { getEventDefinitions: () => LIVE_CYCLE_EVENT_DEFINITIONS } },
];

const FALLBACK_EVENT_DEFINITIONS = [
  // The ten cycle window stages (DWLF-269). Fallbacks are package-LAST by
  // construction: the installed package defines all five stages, and these
  // keep them declared, subscribable and polled if the package call fails. On weekly the stage is the stored window's; on
  // daily/hourly the live state machine's gap buffer.
  { id: 'cycle.low.window.earliest', name: 'Cycle Low Window (Earliest)', description: 'The shortest inter-pivot gap on record has elapsed since the last confirmed low — the earliest the next low could plausibly land (weekly).', category: 'cycles' },
  { id: 'cycle.low.window.early', name: 'Cycle Low Window (Early)', description: 'Early part of the expected next-low window — the 25th percentile of inter-pivot gaps.', category: 'cycles' },
  { id: 'cycle.low.window.mid', name: 'Cycle Low Window (Mid)', description: 'Centre of the expected next-low window — the 50th percentile of inter-pivot gaps.', category: 'cycles' },
  { id: 'cycle.low.window.late', name: 'Cycle Low Window (Late)', description: 'Late part of the expected next-low window — the 75th percentile of inter-pivot gaps.', category: 'cycles' },
  { id: 'cycle.low.window.missed', name: 'Cycle Low Window (Missed)', description: 'The hard max of the expected next-low window has passed with no confirmed low — the window was missed (weekly).', category: 'cycles' },
  { id: 'cycle.high.window.earliest', name: 'Cycle High Window (Earliest)', description: 'The shortest inter-pivot gap on record has elapsed since the last confirmed high — the earliest the next high could plausibly land (weekly).', category: 'cycles' },
  { id: 'cycle.high.window.early', name: 'Cycle High Window (Early)', description: 'Early part of the expected next-high window — the 25th percentile of inter-pivot gaps.', category: 'cycles' },
  { id: 'cycle.high.window.mid', name: 'Cycle High Window (Mid)', description: 'Centre of the expected next-high window — the 50th percentile of inter-pivot gaps.', category: 'cycles' },
  { id: 'cycle.high.window.late', name: 'Cycle High Window (Late)', description: 'Late part of the expected next-high window — the 75th percentile of inter-pivot gaps.', category: 'cycles' },
  { id: 'cycle.high.window.missed', name: 'Cycle High Window (Missed)', description: 'The hard max of the expected next-high window has passed with no confirmed high — the window was missed (weekly).', category: 'cycles' },
  { id: 'trendline_break', name: 'Trendline Break', description: 'Candle close invalidated an existing trendline.', category: 'trendlines' },
  { id: 'trendline_break_bullish', name: 'Trendline Break (Bullish Close)', description: 'Close above resistance trendline.', category: 'trendlines' },
  { id: 'trendline_break_bearish', name: 'Trendline Break (Bearish Close)', description: 'Close below support trendline.', category: 'trendlines' },
  { id: 'trendline_breach', name: 'Trendline Breach', description: 'Intraday wick through a trendline.', category: 'trendlines' },
  { id: 'trendline_breach_bullish', name: 'Trendline Breach (Bullish)', description: 'Intraday wick above resistance trendline.', category: 'trendlines' },
  { id: 'trendline_breach_bearish', name: 'Trendline Breach (Bearish)', description: 'Intraday wick below support trendline.', category: 'trendlines' },
  { id: 'swing_low_formed', name: 'Swing Low Formed', description: 'Confirmed swing low structure.', category: 'swings' },
  { id: 'swing_high_formed', name: 'Swing High Formed', description: 'Confirmed swing high structure.', category: 'swings' },
  { id: 'swing_low_break', name: 'Swing Low Break', description: 'Prior swing low was broken.', category: 'swings' },
  { id: 'swing_high_break', name: 'Swing High Break', description: 'Prior swing high was broken.', category: 'swings' },
  { id: 'swing_low_sweep', name: 'Swing Low Sweep', description: 'Price swept below a swing low and reclaimed above it.', category: 'swings' },
  { id: 'swing_high_sweep', name: 'Swing High Sweep', description: 'Price swept above a swing high and reclaimed below it.', category: 'swings' },
  { id: 'lower_low', name: 'Lower Low', description: 'New lower low swing.', category: 'swings' },
  { id: 'lower_high', name: 'Lower High', description: 'New lower high swing.', category: 'swings' },
  { id: 'higher_low', name: 'Higher Low', description: 'New higher low swing.', category: 'swings' },
  { id: 'higher_high', name: 'Higher High', description: 'New higher high swing.', category: 'swings' },
  { id: 'cycle.low.higher_low', name: 'Higher Cycle Low', description: 'The latest confirmed cycle low is higher than the previous confirmed cycle low.', category: 'cycles' },
  { id: 'cycle.low.lower_low', name: 'Lower Cycle Low', description: 'The latest confirmed cycle low is lower than the previous confirmed cycle low.', category: 'cycles' },
  { id: 'cycle.low.sweep', name: 'Cycle Low Sweep', description: 'Price swept below the most recent confirmed cycle low and reclaimed above it.', category: 'cycles' },
  { id: 'cycle.high.higher_high', name: 'Higher Cycle High', description: 'The latest confirmed cycle high is higher than the previous confirmed cycle high.', category: 'cycles' },
  { id: 'cycle.high.lower_high', name: 'Lower Cycle High', description: 'The latest confirmed cycle high is lower than the previous confirmed cycle high.', category: 'cycles' },
  { id: 'cycle.high.sweep', name: 'Cycle High Sweep', description: 'Price swept above the most recent confirmed cycle high and reclaimed below it.', category: 'cycles' },
  { id: 'ema.cross.above', name: 'EMA Bullish Cross', description: 'Fast EMA crossed above slow EMA.', category: 'movingAverages' },
  { id: 'ema.cross.below', name: 'EMA Bearish Cross', description: 'Fast EMA crossed below slow EMA.', category: 'movingAverages' },
  { id: 'ema_bullish_alignment', name: 'EMA Bullish Alignment', description: 'EMA ribbon turned bullish.', category: 'movingAverages' },
  { id: 'ema_bullish_unalignment', name: 'EMA Bullish Unalignment', description: 'EMA ribbon lost bullish alignment.', category: 'movingAverages' },
  { id: 'ema_bullish_cloud_hit', name: 'EMA Bullish Cloud Hit', description: 'Price tapped bullish EMA cloud.', category: 'movingAverages' },
  { id: 'ema_bearish_alignment', name: 'EMA Bearish Alignment', description: 'EMA ribbon turned bearish.', category: 'movingAverages' },
  { id: 'ema_bearish_unalignment', name: 'EMA Bearish Unalignment', description: 'EMA ribbon lost bearish alignment.', category: 'movingAverages' },
  { id: 'ema_bearish_cloud_hit', name: 'EMA Bearish Cloud Hit', description: 'Price tapped bearish EMA cloud.', category: 'movingAverages' },
  { id: 'ema_bullish_alignment_sustained', name: 'EMA Bullish Alignment Sustained', description: 'Bullish EMA alignment held for the configured duration.', category: 'movingAverages' },
  { id: 'ema_bearish_alignment_sustained', name: 'EMA Bearish Alignment Sustained', description: 'Bearish EMA alignment held for the configured duration.', category: 'movingAverages' },
  { id: 'sma.cross.above', name: 'SMA Bullish Cross', description: 'Fast SMA crossed above slow SMA.', category: 'movingAverages' },
  { id: 'sma.cross.below', name: 'SMA Bearish Cross', description: 'Fast SMA crossed below slow SMA.', category: 'movingAverages' },
  { id: 'bollinger.break.aboveUpper', name: 'Bollinger Break Above Upper', description: 'Price closed above the upper Bollinger band.', category: 'volatility' },
  { id: 'bollinger.break.belowLower', name: 'Bollinger Break Below Lower', description: 'Price closed below the lower Bollinger band.', category: 'volatility' },
  { id: 'atr.regime.expansion', name: 'Volatility Expansion', description: 'ATR% of close reached the top of its recent range.', category: 'volatility' },
  { id: 'atr.regime.contraction', name: 'Volatility Contraction', description: 'ATR% of close reached the bottom of its recent range.', category: 'volatility' },
  { id: 'keyLevel.formed', name: 'Key Level Formed', description: 'The level became known.', category: 'marketStructure' },
  { id: 'keyLevel.approached', name: 'Key Level Approached', description: 'Price closed near the level from its expected side.', category: 'marketStructure' },
  { id: 'keyLevel.tested', name: 'Key Level Tested', description: 'Price reached the level.', category: 'marketStructure' },
  { id: 'keyLevel.rejected', name: 'Key Level Rejected', description: 'Price left the level on its expected side: the level held.', category: 'marketStructure' },
  { id: 'keyLevel.broken', name: 'Key Level Broken', description: 'Price closed beyond the level by the break buffer.', category: 'marketStructure' },
  { id: 'keyLevel.retested', name: 'Key Level Retested', description: 'Price returned to a broken level from the other side.', category: 'marketStructure' },
  { id: 'keyLevel.flipped', name: 'Key Level Flipped', description: 'A broken level held from the other side: its role reversed.', category: 'marketStructure' },
  { id: 'keyLevel.reclaimed', name: 'Key Level Reclaimed', description: 'Price closed back through soon after a break: the break failed.', category: 'marketStructure' },
  { id: 'keyLevel.expired', name: 'Key Level Expired', description: 'The level ended, or went untouched for the expiry period.', category: 'marketStructure' },
  { id: 'dss.cross.bullish', name: 'DSS Bullish Cross', description: 'DSS crossed above its signal line.', category: 'oscillators' },
  { id: 'dss.cross.bearish', name: 'DSS Bearish Cross', description: 'DSS crossed below its signal line.', category: 'oscillators' },
  { id: 'dss.level.overbought', name: 'DSS Entered Overbought', description: 'DSS rose through the overbought threshold.', category: 'oscillators' },
  { id: 'dss.level.oversold', name: 'DSS Entered Oversold', description: 'DSS fell through the oversold threshold.', category: 'oscillators' },
];

let cachedDefinitions;
let cachedDefinitionMap;

const pullDefinitions = () => {
  const deduped = new Map();

  INDICATOR_MODULES.forEach(({ module, category }) => {
    if (!module || typeof module.getEventDefinitions !== 'function') {
      return;
    }
    try {
      const definitions = module.getEventDefinitions();
      if (!Array.isArray(definitions)) {
        return;
      }
      definitions.forEach((definition) => {
        if (!definition?.id || deduped.has(definition.id)) {
          return;
        }
        deduped.set(definition.id, {
          id: definition.id,
          name: definition.name ?? definition.id,
          description: definition.description ?? '',
          category,
        });
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('indicatorEvents: Failed to read indicator event definitions', error);
    }
  });

  FALLBACK_EVENT_DEFINITIONS.forEach((definition) => {
    if (!deduped.has(definition.id)) {
      deduped.set(definition.id, { ...definition });
    }
  });

  const resolved = Array.from(deduped.values());
  resolved.sort((a, b) => a.name.localeCompare(b.name));
  return resolved;
};

export const resetIndicatorEventDefinitionsCache = () => {
  cachedDefinitions = undefined;
  cachedDefinitionMap = undefined;
};

export const getIndicatorEventDefinitions = () => {
  if (!cachedDefinitions) {
    const prepared = pullDefinitions().map((definition) => Object.freeze({ ...definition }));
    cachedDefinitions = Object.freeze(prepared);
  }
  return cachedDefinitions;
};

export const getIndicatorEventDefinitionMap = () => {
  if (!cachedDefinitionMap) {
    cachedDefinitionMap = Object.freeze(
      getIndicatorEventDefinitions().reduce((acc, definition) => {
        acc[definition.id] = definition;
        return acc;
      }, {}),
    );
  }
  return cachedDefinitionMap;
};

const DEFAULT_EVENT_LOOKBACK_DAYS = 5;
// Divergence rows (DWLF-338) are dated by their later pivot but knowable only at its confirm bar,
// and the jobs store late-published pivots up to 30 bars back: look back past that (calendar days).
const DIVERGENCE_LOOKBACK_DAYS = 45;
const DIVERGENCE_LOOKBACKS = Object.fromEntries(
  DIVERGENCE_EVENT_DEFINITIONS.map((definition) => [definition.id, DIVERGENCE_LOOKBACK_DAYS]),
);

const EVENT_LOOKBACK_OVERRIDES = {
  ...DIVERGENCE_LOOKBACKS,
  trendline_break: 3,
  trendline_break_bullish: 3,
  trendline_break_bearish: 3,
  trendline_breach: 3,
  trendline_breach_bullish: 3,
  trendline_breach_bearish: 3,
  swing_high_formed: 10,
  swing_low_formed: 10,
  swing_high_break: 5,
  swing_low_break: 5,
  swing_high_sweep: 5,
  swing_low_sweep: 5,
  higher_high: 10,
  higher_low: 10,
  lower_high: 10,
  lower_low: 10,
  'cycle.low.higher_low': 120,
  'cycle.low.lower_low': 120,
  'cycle.low.sweep': 120,
  'cycle.high.higher_high': 120,
  'cycle.high.lower_high': 120,
  'cycle.high.sweep': 120,
  // A window stage stays the current reading until the next one crosses,
  // on every timeframe; on weekly that is bars apart, and
  // the next stage; the 5-day default would drop one before its next bar.
  'cycle.low.window.earliest': 120,
  'cycle.low.window.early': 120,
  'cycle.low.window.mid': 120,
  'cycle.low.window.late': 120,
  'cycle.low.window.missed': 120,
  'cycle.high.window.earliest': 120,
  'cycle.high.window.early': 120,
  'cycle.high.window.mid': 120,
  'cycle.high.window.late': 120,
  'cycle.high.window.missed': 120,
};

export const getEventLookbackDays = (eventType) =>
  EVENT_LOOKBACK_OVERRIDES[eventType] ?? DEFAULT_EVENT_LOOKBACK_DAYS;
